import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@auth";
import { createUiPreference, updateUiPreference, useUiPreferences } from "@api";
import { toast } from "@components/toast/Toast";

export const HOME_LAYOUT_TYPE = "homeLayout:tasks";
export const HOME_SECTIONS = ["burndown", "workingSets", "suggestions"];

// Stored order first, then any section the stored order doesn't know about yet in its default place,
// so adding a section later never hides it from people who already rearranged Home.
export const normaliseHomeOrder = (stored) => {
  const known = Array.isArray(stored)
    ? stored.filter((id, i) => HOME_SECTIONS.includes(id) && stored.indexOf(id) === i)
    : [];
  return [...known, ...HOME_SECTIONS.filter((id) => !known.includes(id))];
};

export const parseHomeLayout = (preference) => {
  if (!preference) return normaliseHomeOrder(null);
  try {
    return normaliseHomeOrder(JSON.parse(preference.value)?.order);
  } catch {
    return normaliseHomeOrder(null);
  }
};

export const useHomeLayout = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data: preferences = [] } = useUiPreferences();
  const preference = preferences.find((p) => p.type === HOME_LAYOUT_TYPE) ?? null;
  const stored = useMemo(() => parseHomeLayout(preference), [preference]);
  // The dropped order renders in the same frame as the drop; the query cache only notifies on a later tick
  // (or after the server replies), and dnd-kit has already cleared the drag transform by then.
  const [pending, setPending] = useState(null);
  const order = pending ?? stored;

  const cacheKey = ["uiPreferences", user?.guid];
  const patchCache = (updater) => queryClient.setQueryData(cacheKey, (old) => updater(old ?? []));

  const persist = async (value) => {
    if (preference) return updateUiPreference(preference.id, { name: preference.name, value });
    const created = await createUiPreference({ name: "Home layout", type: HOME_LAYOUT_TYPE, value });
    if (created) patchCache((old) => [...old.filter((p) => p.id !== created.id), created]);
    return !!created;
  };

  const save = async (next) => {
    setPending(next);
    const value = JSON.stringify({ order: next });
    if (preference) patchCache((old) => old.map((p) => (p.id === preference.id ? { ...p, value } : p)));
    if (await persist(value)) return;
    setPending(null);
    toast({ description: "Couldn't save your Home layout. Please try again." });
    queryClient.invalidateQueries({ queryKey: ["uiPreferences"] });
  };

  return { order, save };
};
